/**
 * Gestionnaire générique pour les règles de conversion avec regex
 * Fournit une API pour construire et utiliser des parsers basés sur des règles regex
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

// Registre global pour stocker tous les helpers créés
const helpersRegistry = {
  helpers: {},
  registerHelper: function(helper) {
    this.helpers[helper.name] = helper;
    debug.log(config, `RegexpHelperRegistry: Enregistrement du helper "${helper.name}"`);
    return helper;
  },
  getHelper: function(name) {
    return this.helpers[name];
  },
  getAllHelpers: function() {
    return this.helpers;
  }
};

// Exporter le registre pour qu'il soit accessible par d'autres modules
export const regexpHelpersRegistry = helpersRegistry;

/**
 * Crée un nouvel objet RegexpHelper pour gérer un ensemble de règles de conversion
 * @param {string} name - Nom du helper (pour les logs)
 * @returns {Object} - Instance du RegexpHelper
 */
export const createRegexpHelper = (name) => {
  const rules = [];
  let isInitialized = false;

  /**
   * Ajoute une règle compilée à la collection
   * @param {string} searchPattern - Pattern de recherche
   * @param {string} replacePattern - Pattern de remplacement
   * @param {string} source - Source de la règle (API, localStorage, etc.)
   * @param {Object} metadata - Métadonnées optionnelles
   */
  const addCompiledRule = (searchPattern, replacePattern, source, metadata = {}) => {
    try {
      const compiledRegex = new RegExp(searchPattern, 'gis');
      
      rules.push({
        source,
        searchPattern,
        replacePattern,
        compiledRegex,
        metadata: {
          ...metadata,
          compiledAt: new Date().toISOString()
        }
      });
      
      debug.log(config, `${name}: Règle compilée ajoutée [${source}]: ${searchPattern} > ${replacePattern}`);
      
    } catch (regexError) {
      debug.error(config, `${name}: Erreur compilation regex [${source}] ${searchPattern}:`, regexError.message);
      // On ajoute quand même la règle mais sans regex compilée pour le debug
      rules.push({
        source,
        searchPattern,
        replacePattern,
        compiledRegex: null,
        error: regexError.message,
        metadata: {
          ...metadata,
          compilationFailed: true,
          errorAt: new Date().toISOString()
        }
      });
    }
  };

  /**
   * Fonction générique pour appliquer des règles de conversion sur un texte
   * @param {string} text - Texte à traiter
   * @param {string} direction - Direction de conversion (pour les logs)
   * @returns {string} - Texte traité
   */
  const applyConversionRules = (text, direction = 'conversion') => {
    if (!text) return '';

    debug.log(config, `${name}: Début du traitement ${direction} - ${rules.length} règles disponibles, texte: ${text.length} caractères`, text.length > 150 ? text.substring(0, 150) + '...' : text);
    
    try {
      let processedText = text;
      let stats = {
        total: 0,
        applied: 0,
        notCompiled: 0,
        error: 0
      };
      
      // Appliquer chaque règle
      for (const rule of rules) {
        stats.total++;
        
        if (!rule.compiledRegex) {
          stats.notCompiled++;
          debug.warn(config, `${name}: Règle ${direction} ignorée (regex non compilée): ${rule.searchPattern} > ${rule.replacePattern}`);
          continue;
        }
        
        try {
          const before = processedText;
          processedText = processedText.replace(rule.compiledRegex, rule.replacePattern);
          
          // Vérifier si la règle a eu un effet
          if (before !== processedText) {
            stats.applied++;
            debug.log(config, `${name}: Règle ${direction} appliquée avec effet [${rule.source}]: ${rule.searchPattern} > ${rule.replacePattern}`);
          }
        } catch (regexError) {
          stats.error++;
          debug.warn(config, `${name}: Erreur application règle ${direction} [${rule.source}] ${rule.searchPattern}: > ${rule.replacePattern}`, regexError.message);
        }
      }
      
      debug.log(config, `${name}: Traitement ${direction} terminé - Stats: ${stats.total} règles (${stats.applied} appliquées, ${stats.notCompiled} non compilées, ${stats.error} en erreur)`);
      return processedText;
      
    } catch (error) {
      debug.error(config, `${name}: Erreur lors du traitement ${direction}:`, error);
      return text;
    }
  };

  /**
   * Charge les règles depuis un tableau d'objets
   * @param {Array} rulesArray - Tableau de règles avec {search_pattern, replace_pattern}
   * @param {string} source - Source des règles
   * @param {Object} defaultMetadata - Métadonnées par défaut
   */
  const loadRulesFromArray = (rulesArray, source, defaultMetadata = {}) => {
    debug.log(config, `${name}: Chargement de ${rulesArray.length} règles depuis ${source}`);
    
    for (const rule of rulesArray) {
      if (rule.search_pattern) {
        addCompiledRule(
          rule.search_pattern,
          rule.replace_pattern,
          source,
          { ...defaultMetadata, ...rule.metadata }
        );
      }
    }
  };

  /**
   * Charge les règles depuis le localStorage
   * @param {string} key - Clé localStorage
   * @param {Function} transformer - Fonction pour transformer les données localStorage en règles
   */
  const loadRulesFromLocalStorage = (key, transformer) => {
    try {
      const dataStr = localStorage.getItem(key);
      if (dataStr) {
        const data = JSON.parse(dataStr);
        const transformedRules = transformer(data);
        
        for (const rule of transformedRules) {
          addCompiledRule(
            rule.searchPattern,
            rule.replacePattern,
            `localStorage:${key}`,
            rule.metadata
          );
        }
        
        debug.log(config, `${name}: ${transformedRules.length} règles chargées depuis localStorage:${key}`);
      }
    } catch (error) {
      debug.warn(config, `${name}: Erreur lors du chargement depuis localStorage:${key}:`, error);
    }
  };

  /**
   * Réinitialise toutes les règles
   */
  const clearRules = () => {
    rules.length = 0;
    isInitialized = false;
    debug.log(config, `${name}: Règles réinitialisées`);
  };

  /**
   * Retourne les règles sous forme d'objet pour compatibilité
   */
  const getRules = () => {
    const rulesObject = {};
    rules.forEach(rule => {
      rulesObject[rule.searchPattern] = {
        replacePattern: rule.replacePattern,
        source: rule.source,
        metadata: rule.metadata,
        hasCompiledRegex: !!rule.compiledRegex,
        error: rule.error
      };
    });
    return rulesObject;
  };

  /**
   * Retourne les règles sous forme de tableau
   */
  const getRulesArray = () => {
    return [...rules];
  };

  /**
   * Marque le helper comme initialisé
   */
  const setInitialized = (value = true) => {
    isInitialized = value;
  };

  /**
   * Vérifie si le helper est initialisé
   */
  const getInitialized = () => {
    return isInitialized;
  };

  /**
   * Retourne le nombre de règles
   */
  const getRulesCount = () => {
    return rules.length;
  };

  // Interface publique du RegexpHelper
  const helper = {
    name,
    addCompiledRule,
    applyConversionRules,
    loadRulesFromArray,
    loadRulesFromLocalStorage,
    clearRules,
    getRules,
    getRulesArray,
    setInitialized,
    getInitialized,
    getRulesCount
  };
  
  // Enregistrer automatiquement le helper dans le registre global
  return helpersRegistry.registerHelper(helper);
};

/**
 * Factory pour créer des helpers spécialisés
 */
export const createSpecializedHelper = (name, loaderFunction) => {
  const helper = createRegexpHelper(name);
  
  const specializedHelper = {
    ...helper,
    /**
     * Initialise le helper avec sa fonction de chargement spécifique
     */
    initialize: async () => {
      if (helper.getInitialized()) {
        debug.log(config, `${name}: Helper déjà initialisé`);
        return;
      }

      try {
        debug.log(config, `${name}: Début de l'initialisation`);
        await loaderFunction(helper);
        helper.setInitialized(true);
        debug.log(config, `${name}: Helper initialisé avec succès (${helper.getRulesCount()} règles)`);
      } catch (error) {
        debug.error(config, `${name}: Erreur lors de l'initialisation:`, error);
        helper.setInitialized(false);
        throw error;
      }
    },

    /**
     * Recharge les règles
     */
    reload: async () => {
      debug.log(config, `${name}: Rechargement des règles`);
      helper.setInitialized(false);
      helper.clearRules();
      await specializedHelper.initialize();
    }
  };
  
  // Remplacer l'entrée dans le registre avec la version spécialisée
  return helpersRegistry.registerHelper(specializedHelper);
};
